import React, { useMemo } from "react";

const isBlank = (v) => (v ?? "").toString().trim().length === 0;

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const getAlignValue = (style, fallback = "left") => {
  const v = String(style?.textAlign || style?.alignment || "").toLowerCase().trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return fallback;
};

const getAlignClass = (style, fallback = "left") => {
  const a = getAlignValue(style, fallback);
  if (a === "center") return "text-center";
  if (a === "right") return "text-right";
  return "text-left";
};

const getSelfAlignClass = (style, fallback = "left") => {
  const a = getAlignValue(style, fallback);
  if (a === "center") return "mx-auto";
  if (a === "right") return "ml-auto";
  return "mr-auto";
};

const parseItems = (config) => {
  if (Array.isArray(config?.items)) return config.items;

  const raw = config?.itemsJson;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const openCatalog = (catalogId, baseUrl) => {
  const id = (catalogId || "").trim();
  if (!id) return;
  const base = (baseUrl || "https://smartcatalog.shop").replace(/\/+$/, "");
  window.open(`${base}/${encodeURIComponent(id)}`, "_blank", "noopener,noreferrer");
};

export default function CategorySection3({ config, style }) {
  const bg = buildBg(style, "bg-gradient-to-b from-gray-50 to-white");

  const fallbackAlign = (config?.direction || "ltr") === "rtl" ? "right" : "left";
  const alignClass = getAlignClass(style, fallbackAlign);
  const selfAlignClass = getSelfAlignClass(style, fallbackAlign);

  const items = useMemo(() => parseItems(config), [config]);
  const maxItems = Number.isFinite(Number(config?.maxItems)) ? Number(config.maxItems) : 6;
  const shown = items.slice(0, Math.max(1, maxItems));

  const featured = shown[0];
  const rest = shown.slice(1);

  const buttonText = config?.buttonText ?? "Go to Full Catalog";
  const baseUrl = config?.baseUrl || "https://smartcatalog.shop";

  const btnStyle = {
    backgroundColor: style?.primaryBtnBg || undefined,
    color: style?.primaryBtnText || undefined,
  };

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`py-16 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className={alignClass}>
            <h2
              style={{ color: style?.titleColor || undefined }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-5"
            >
              {config?.title}
            </h2>

            <p
              style={{ color: style?.subtitleColor || undefined }}
              className={`text-xl text-gray-600 mb-8 max-w-2xl ${selfAlignClass}`}
            >
              {config?.subtitle}
            </p>

            <div className="space-y-4">
              {rest.map((it, idx) => (
                <button
                  key={it.id || idx}
                  type="button"
                  onClick={() => openCatalog(config?.catalogId, baseUrl)}
                  style={{
                    backgroundColor: style?.categoryCardBg || undefined,
                    borderColor: style?.categoryCardBorder || undefined,
                  }}
                  className={`w-full ${alignClass} rounded-2xl border shadow-sm hover:shadow-lg transition-all duration-300 p-4 flex items-center gap-4`}
                >
                  <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                    {it.image ? (
                      <img
                        src={it.image}
                        alt={it.title || "item"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200" />
                    )}
                  </div>

                  <div className="min-w-0">
                    <div
                      style={{ color: style?.categoryCardTitleColor || undefined }}
                      className="font-bold text-gray-900 truncate"
                    >
                      {it.title || config?.fallbackItemTitle || ""}
                    </div>

                    {!isBlank(it.subtitle) && (
                      <div
                        style={{ color: style?.categoryCardSubtitleColor || undefined }}
                        className="text-sm text-gray-600 line-clamp-1"
                      >
                        {it.subtitle}
                      </div>
                    )}
                  </div>

                  <div
                    style={{ color: style?.categoryLinkColor || undefined }}
                    className="ms-auto font-semibold"
                  >
                    →
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-10">
              {!isBlank(config?.catalogId) && !isBlank(buttonText) && (
                <button
                  type="button"
                  style={style?.primaryBtnBg ? btnStyle : undefined}
                  onClick={() => openCatalog(config?.catalogId, baseUrl)}
                  className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 hover:scale-105 ${selfAlignClass} ${
                    style?.primaryBtnBg
                      ? ""
                      : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-xl"
                  }`}
                >
                  {buttonText}
                </button>
              )}

              {!isBlank(config?.hintText) && (
                <div
                  style={{ color: style?.categoryCardSubtitleColor || undefined }}
                  className="text-sm text-gray-500 mt-3"
                >
                  {config?.hintText}
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <div
              style={{
                backgroundColor: style?.categoryCardBg || undefined,
                borderColor: style?.categoryCardBorder || undefined,
              }}
              className="rounded-3xl overflow-hidden shadow-2xl border"
            >
              <div className="relative h-[420px] bg-gray-100">
                {featured?.image ? (
                  <img
                    src={featured.image}
                    alt={featured.title || "featured"}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200" />
                )}

                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      style?.categoryImageOverlayColor ||
                      "linear-gradient(to top, rgba(0,0,0,0.60), rgba(0,0,0,0.10), transparent)",
                  }}
                />

                <div className={`absolute bottom-0 left-0 right-0 p-8 text-white ${alignClass}`}>
                  <div
                    style={{
                      backgroundColor: style?.categoryFeaturedBadgeBg || undefined,
                      color: style?.categoryFeaturedBadgeText || undefined,
                      borderColor: style?.categoryFeaturedBadgeBorder || undefined,
                    }}
                    className={`inline-flex items-center px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-semibold mb-4 ${selfAlignClass}`}
                  >
                    {config?.featuredBadgeText || ""}
                  </div>

                  <div
                    style={{ color: style?.categoryCardTitleColor || "#ffffff" }}
                    className="text-3xl font-bold"
                  >
                    {featured?.title || config?.fallbackFeaturedTitle || ""}
                  </div>

                  {!isBlank(featured?.subtitle) && (
                    <div
                      style={{ color: style?.categoryCardSubtitleColor || "rgba(255,255,255,0.80)" }}
                      className="mt-2"
                    >
                      {featured.subtitle}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: style?.categoryFeaturedPanelBg || undefined,
                  borderColor: style?.categoryFeaturedPanelBorder || undefined,
                }}
                className="p-6 border-t"
              >
                <button
                  type="button"
                  onClick={() => openCatalog(config?.catalogId, baseUrl)}
                  style={style?.primaryBtnBg ? btnStyle : undefined}
                  className={`w-full font-bold py-4 px-8 rounded-2xl transition-all duration-300 ${
                    style?.primaryBtnBg ? "" : "bg-black text-white hover:bg-gray-900"
                  }`}
                >
                  {config?.viewInCatalogText || ""}
                </button>
              </div>
            </div>

            <div
              style={{
                backgroundColor: style?.categoryMetricBoxBg || undefined,
                borderColor: style?.categoryMetricBoxBorder || undefined,
              }}
              className="absolute bottom-14 start-2 sm:bottom-4 sm:start-4 md:-bottom-6 md:-start-6 rounded-2xl shadow-xl p-5 border max-w-[calc(100%-2rem)]"
            >
              <div
                style={{ color: style?.categoryMetricLabelColor || undefined }}
                className="text-sm text-gray-500"
              >
                {config?.demoItemsLabel || ""}
              </div>
              <div
                style={{ color: style?.categoryMetricValueColor || undefined }}
                className="text-2xl font-bold text-gray-900"
              >
                {shown.length}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}